import { FileText, ZoomIn, ZoomOut, ChevronsUp, Loader2 } from "lucide-react";
import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { cn } from "@/modern/utils";
import { Button } from "./ui/button";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface PdfViewerProps {
  fileUrl: string;
  fileName?: string;
  showName?: boolean;
  className?: string;
  onLoadSuccess?: () => void;
  onLoadError?: (error: Error) => void;
  isThumbnail?: boolean;
  searchText?: string | null;
}

/** Escapes special regex characters in a string for safe use in `new RegExp()`. */
const escapeRegExp = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const SEARCH_HIGHLIGHT_CLASS = "swivu-pdf-search-highlight";

const PdfViewer = ({ fileUrl, fileName, showName = false, className = "", onLoadSuccess, onLoadError, isThumbnail = false, searchText }: PdfViewerProps) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pdfScale, setPdfScale] = useState<number>(1.0);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Measure container width for responsive PDF rendering
  useEffect(() => {
    const updateWidth = () => {
      if (pdfContainerRef.current) {
        // For thumbnails, use full container width; for full viewer, account for padding
        const width = isThumbnail
          ? pdfContainerRef.current.clientWidth
          : pdfContainerRef.current.clientWidth - 48;
        setContainerWidth(width > 0 ? width : 600);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [isThumbnail]);

  const handleDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setCurrentPage(1);
      setPdfError(null);
      pageRefs.current = new Array(numPages);
      onLoadSuccess?.();
    },
    [onLoadSuccess]
  );

  const handleDocumentLoadError = useCallback(
    (error: Error) => {
      console.error("PDF load error:", error);
      setPdfError("Failed to load PDF");
      onLoadError?.(error);
    },
    [onLoadError]
  );

  // Track current page based on scroll position
  useEffect(() => {
    if (!scrollContainerRef.current || isThumbnail) return;

    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const scrollTop = container.scrollTop;

      // Show scroll to top button after scrolling down
      setShowScrollTop(scrollTop > 500);

      // Find which page is most visible
      let maxVisibleArea = 0;
      let mostVisiblePage = 1;

      pageRefs.current.forEach((pageElement, index) => {
        if (!pageElement) return;

        const rect = pageElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate visible area of this page
        const visibleTop = Math.max(rect.top, containerRect.top);
        const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
        const visibleArea = Math.max(0, visibleBottom - visibleTop);

        if (visibleArea > maxVisibleArea) {
          maxVisibleArea = visibleArea;
          mostVisiblePage = index + 1;
        }
      });

      setCurrentPage(mostVisiblePage);
    };

    const container = scrollContainerRef.current;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [numPages, isThumbnail]);

  // Keyboard shortcuts
  useEffect(() => {
    if (isThumbnail) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        handleZoomIn(e as any);
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        handleZoomOut(e as any);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isThumbnail]);

  const scrollToTop = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleZoomIn = useCallback((e: React.MouseEvent<HTMLButtonElement> | KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setPdfScale((prev) => Math.min(prev + 0.2, 3));
  }, []);

  const handleZoomOut = useCallback((e: React.MouseEvent<HTMLButtonElement> | KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setPdfScale((prev) => Math.max(prev - 0.2, 0.5));
  }, []);

  const resetZoom = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setPdfScale(1.0);
  }, []);

  // --- Search highlight via customTextRenderer (react-pdf official API) ---
  const normalizedSearch = useMemo(
    () => (searchText ? searchText.toLowerCase().trim() : null),
    [searchText]
  );

  const searchWords = useMemo(() => {
    if (!normalizedSearch) return [];
    return normalizedSearch.split(/\s+/).filter((w) => w.length > 2).slice(0, 8);
  }, [normalizedSearch]);

  const customTextRenderer = useCallback(
    (textItem: { str: string; itemIndex: number }): string => {
      if (!normalizedSearch) return textItem.str;

      const str = textItem.str;
      const lowerStr = str.toLowerCase();

      // Strategy 1: The search text appears inside this span
      if (lowerStr.includes(normalizedSearch)) {
        return str.replace(
          new RegExp(`(${escapeRegExp(searchText!)})`, "gi"),
          `<mark class="${SEARCH_HIGHLIGHT_CLASS}">$1</mark>`
        );
      }

      // Strategy 2: This span's text is a substring of the search text (multi-span match)
      if (normalizedSearch.includes(lowerStr) && lowerStr.trim().length > 2) {
        return `<mark class="${SEARCH_HIGHLIGHT_CLASS}">${str}</mark>`;
      }

      // Strategy 3: Word-based fallback — highlight individual matching words
      if (searchWords.length > 0 && searchWords.some((word) => lowerStr.includes(word))) {
        let result = str;
        for (const word of searchWords) {
          result = result.replace(
            new RegExp(`(${escapeRegExp(word)})`, "gi"),
            `<mark class="${SEARCH_HIGHLIGHT_CLASS}">$1</mark>`
          );
        }
        return result;
      }

      return str;
    },
    [normalizedSearch, searchWords, searchText]
  );

  // Scroll the first highlight into view after react-pdf renders the marks
  useEffect(() => {
    if (!searchText || !pdfContainerRef.current) return;

    const timer = setTimeout(() => {
      const firstMark = pdfContainerRef.current?.querySelector(`mark.${SEARCH_HIGHLIGHT_CLASS}`);
      if (firstMark) {
        firstMark.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Thumbnail mode: simplified UI
  if (isThumbnail) {
    return (
      <div ref={pdfContainerRef} className={cn("relative w-full h-full flex items-center justify-center", className)}>
        {pdfError ? (
          <div className="flex flex-col items-center justify-center text-muted-foreground">
            <FileText className="size-12 mb-2" />
            <span className="text-xs">{pdfError}</span>
          </div>
        ) : (
          <>
            <Document
              file={fileUrl}
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={handleDocumentLoadError}
              loading={
                <div className="flex items-center justify-center py-16 text-xs gap-2">
                  <Loader2 className="size-4 animate-spin" /> Loading...
                </div>
              }
            >
              <Page
                pageNumber={1}
                width={containerWidth > 0 ? containerWidth : undefined}
                height={pdfContainerRef.current?.clientHeight || undefined}
                loading={
                  <div className="flex items-center justify-center w-full h-full">
                    <div className="animate-spin rounded-full size-6 border-2 border-primary border-t-transparent" />
                  </div>
                }
                className="!w-full !h-full"
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
            {/* Page count badge - scales with container size */}
            {numPages > 1 && (
              <div
                className={cn(
                  "absolute bg-black/70 text-white rounded-md bottom-1 right-1 text-xs px-1 py-1.5",
                  containerWidth < 150 ? "bottom-1 right-0.5 text-[.625rem] px-1 py-0.5" : containerWidth < 250 ? "bottom-1 right-1 text-[.75rem] px-1.5 py-0.5" : "bottom-2 right-2 text-xs px-2 py-1"
                )}
              >
                {numPages} pages
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Full viewer mode: Continuous scroll with all pages
  return (
    <div ref={pdfContainerRef} className={cn("relative flex flex-col w-full h-full", className)}>
      {/* PDF Document - Scrollable container with all pages */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-auto scroll-smooth">
        <div className="flex flex-col items-center py-6 px-4 gap-4 min-w-min">
          {pdfError ? (
            <div className="flex flex-col items-center justify-center text-muted-foreground py-16">
              <FileText className="size-16 mb-4 opacity-50" />
              <span className="text-sm font-medium">{pdfError}</span>
            </div>
          ) : (
            <Document
              file={fileUrl}
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={handleDocumentLoadError}
              loading={
                <div className="flex items-center justify-center py-16 gap-2 text-sm absolute inset-0">
                  <Loader2 className="size-4 animate-spin" /> Loading...
                </div>
              }
            >
              {containerWidth > 0 && numPages > 0 && (
                <>
                  {Array.from(new Array(numPages), (_, index) => (
                    <div key={`page_${index + 1}`} ref={(el) => (pageRefs.current[index] = el)}>
                      <Page
                        pageNumber={index + 1}
                        scale={pdfScale}
                        width={containerWidth}
                        loading={
                          <div
                            className="flex items-center justify-center bg-muted/20 rounded-lg border"
                            style={{
                              width: containerWidth * pdfScale,
                              height: containerWidth * pdfScale * 1.414 // A4 aspect ratio
                            }}
                          >
                            <div className="animate-spin rounded-full size-8 border-2 border-primary border-t-transparent" />
                          </div>
                        }
                        className="shadow-xl rounded-lg overflow-hidden border border-border/50 transition-all duration-300 hover:shadow-2xl"
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        customTextRenderer={normalizedSearch ? customTextRenderer : undefined}
                      />

                      {/* Subtle page divider */}
                      {index < numPages - 1 && (
                        <div className="mt-4 flex items-center justify-center opacity-30">
                          <div className="h-px w-32 bg-gradient-to-r from-transparent via-border to-transparent" />
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </Document>
          )}
        </div>
      </div>

      {showName && fileName && (
        <div className="flex-shrink-0 border-t border-border bg-background/95 backdrop-blur-sm">
          <span className="block text-sm text-center py-3 px-4 font-medium truncate">{fileName}</span>
        </div>
      )}

      {/* Floating Controls - Compact Bottom Right Widget */}
      {!pdfError && numPages > 0 && (
        <div className="absolute bottom-4 right-4 flex flex-col gap-2 items-end z-10">
          {/* Scroll to Top Button - Separate floating button */}
          {showScrollTop && (
            <Button variant="outline" size="icon" onClick={scrollToTop} aria-label="Scroll to top" title="Scroll to top">
              <ChevronsUp className="size-4 text-primary-foreground transition-transform group-hover:-translate-y-0.5" />
            </Button>
          )}

          {/* Compact Control Panel */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-background/95 backdrop-blur-md border shadow-lg">
            {/* Page Indicator - Compact */}
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/50 text-xs font-medium min-w-[60px] justify-center">
              <span className="text-foreground">{currentPage}</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-muted-foreground">{numPages}</span>
            </div>

            {/* Divider */}
            <div className="w-px h-4 bg-border" />

            {/* Zoom Out */}
            <button
              onClick={handleZoomOut}
              disabled={pdfScale <= 0.5}
              className="size-7 rounded-full hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center group"
              aria-label="Zoom out"
              title="Zoom out (-)"
            >
              <ZoomOut className="size-3.5 text-muted-foreground group-hover:text-foreground group-hover:scale-110 transition-all" />
            </button>

            {/* Zoom Level - Clickable to reset */}
            <button
              onClick={resetZoom}
              className="min-w-[42px] px-2 py-0.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-full transition-all duration-200"
              title="Reset zoom"
            >
              {Math.round(pdfScale * 100)}%
            </button>

            {/* Zoom In */}
            <button
              onClick={handleZoomIn}
              disabled={pdfScale >= 3}
              className="size-7 rounded-full hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center group"
              aria-label="Zoom in"
              title="Zoom in (+)"
            >
              <ZoomIn className="size-3.5 text-muted-foreground group-hover:text-foreground group-hover:scale-110 transition-all" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfViewer;
